import React, { useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { NotificationsPage } from './NotificationsPage';
import { NotificationPage } from './NotificationPage';

/**
 * Page shell for notifications, mirroring ProviderCommentsPageContainer (the
 * Activity/notes shell) so notifications behave like every other main section of
 * the site: a green header bar, tabs underneath it, and a dynamic tab for the
 * record you have open that you can click away from and back to.
 *
 * Routes: /notifications → the list; /notifications/:id → the list's tab plus a
 * tab for that notification. The green bar lives here rather than in either page,
 * so both share one header exactly as Activity Notes / Stark / Mileage / Reports
 * share theirs.
 */

// Long announcement titles would blow out the tab bar; the notes container has it
// easy because it labels its detail tab with a date.
const tabLabel = (n) => {
  if (!n || !n.title) return 'Notification';
  return n.title.length > 32 ? `${n.title.slice(0, 32)}…` : n.title;
};

const NotificationsPageContainer = ({ tab }) => {
  const navigate = useNavigate();
  const currentTab = tab || 'list';
  // Only used to label the open notification's tab. Cleared on the way back to the
  // list so the tab disappears with it — the same way the note detail tab goes away
  // once you're back on Activity Notes.
  const [openNotification, setOpenNotification] = useState(null);

  const onTabChange = useCallback((e, value) => {
    if (value === 'list') {
      setOpenNotification(null);
      navigate('/notifications');
    } else if (openNotification) {
      navigate(`/notifications/${openNotification.id}`);
    }
  }, [navigate, openNotification]);

  return (
    <>
      <title>
        {currentTab === 'detail' && openNotification
          ? `Notification - ${openNotification.title}`
          : 'Notifications'}
      </title>

      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            p: 3,
            pb: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'baseline',
            backgroundColor: 'primary.accent',
          }}
        >
          <Typography sx={{ color: '#FFFFFF', fontSize: 24, pr: '30px' }}>
            Notifications
          </Typography>
        </Box>
        <Tabs
          variant='scrollable'
          sx={{ pt: '3px', pl: 3, backgroundColor: 'primary.accent' }}
          TabIndicatorProps={{ sx: { backgroundColor: '#FFFFFF', height: 2, mb: '8px' } }}
          value={currentTab}
          onChange={onTabChange}
        >
          <Tab
            sx={{
              color: '#E0E0E0',
              '&.Mui-selected': { color: '#FFFFFF' },
              '&:hover': { color: '#FFFFFF' },
            }}
            label='All Notifications'
            value='list'
            disableRipple
          />
          {/* Present only while a notification is actually open. Leaving the tab
              up after you return to the list would strand a pointer to something
              you've navigated away from. */}
          {currentTab === 'detail' && (
            <Tab
              sx={{
                color: '#E0E0E0',
                '&.Mui-selected': { color: '#FFFFFF' },
                '&:hover': { color: '#FFFFFF' },
              }}
              label={tabLabel(openNotification)}
              value='detail'
              disableRipple
            />
          )}
        </Tabs>
      </Box>

      {currentTab === 'list' && <NotificationsPage />}
      {currentTab === 'detail' && <NotificationPage onLoaded={setOpenNotification} />}
    </>
  );
};

NotificationsPageContainer.propTypes = {
  tab: PropTypes.string,
};

export { NotificationsPageContainer };
export default NotificationsPageContainer;
